import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { CENOX_HOME, nextFrom, withNext } from '../../lib/intent';
import { ApiError } from '../../lib/api';
import { Button, Input } from '../../components/ui';
import { AuthLayout } from './AuthLayout';

export function LoginPage(): ReactNode {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const next = nextFrom(location.search);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await login(email, password);
      /*
        Where they were going, or CenoX Home. Having no business is not a
        reason to send anybody to business onboarding — CenoX Business offers
        that itself when they open it.
      */
      navigate(next ?? CENOX_HOME, { replace: true });
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Could not sign in. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to your CenoX account"
      footer={
        <>
          New to CenoX?{' '}
          <Link to={withNext('/register', next)} className="font-medium text-brand-600 hover:text-brand-700">
            Create an account
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {new URLSearchParams(location.search).get('deleted') === '1' && !error && (
          <div role="status" className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
            <p className="text-sm text-slate-700">Your CenoX account has been deleted.</p>
          </div>
        )}

        {error && (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <Input
          label="Email"
          type="email"
          name="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />

        <Input
          label="Password"
          type="password"
          name="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />

        {/*
          Without this the reset screen is reachable only from an email nobody
          can ask for — the flow existed end to end except for its first step.
        */}
        <p className="-mt-1 text-right text-sm">
          <Link
            to="/forgot-password"
            className="font-medium text-brand-600 hover:text-brand-700"
          >
            Forgot your password?
          </Link>
        </p>

        <Button type="submit" size="lg" loading={submitting} className="w-full">
          Sign in
        </Button>
      </form>
    </AuthLayout>
  );
}
